import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Clock, Trash2, Plus, Download, Users, Building2, RefreshCw } from "lucide-react";
import { MOVEMENT_TYPE_LABEL, SETTLEMENT_STATUS_LABEL } from "@/lib/i18n";
import { DayOffDebt } from "@/pages/shared/DayOffDebt";
import { SiteSelect } from "@/components/SiteSelect";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthAgoStr = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

function fmtScheduleTurno(t) {
  if (!t) return "sin turno en el cronograma";
  if (t.is_day_off) return "FRANCO";
  const site = t.site_name ? ` · ${t.site_name}` : "";
  if (t.is_coverage_override) return `${t.client_name}${site} ${t.time_from}\u2013${t.time_to} (cobertura)`;
  return `${t.client_name}${site} ${t.time_from}\u2013${t.time_to} (${t.hours} h)`;
}

export default function WorkHours() {
  const { user } = useAuth();
  const backLink = user?.role === "admin" ? "/admin" : "/supervisor";
  const [clients, setClients] = useState([]);
  const [people, setPeople] = useState([]);
  const [logs, setLogs] = useState([]);
  const [totalsUser, setTotalsUser] = useState([]);
  const [totalsClient, setTotalsClient] = useState([]);
  const [busy, setBusy] = useState(false);

  const empty = { client_id: "", site_id: "", user_id: "", date: todayStr(), hours_worked: "", movement_type: "jornada_normal", manual: false, normal_hours_manual: "", overtime_hours_manual: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [filters, setFilters] = useState({ client_id: "", user_id: "", date_from: monthAgoStr(), date_to: todayStr(), settlement_status: "" });
  const [tab, setTab] = useState("jornadas");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  useEffect(() => { (async () => {
    try {
      const [c, p] = await Promise.all([api.get("/clients"), api.get("/users/roster")]);
      setClients(c.data); setPeople(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const refresh = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.client_id) params.set("client_id", filters.client_id);
      if (filters.user_id) params.set("user_id", filters.user_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.settlement_status) params.set("settlement_status", filters.settlement_status);
      const { data } = await api.get(`/work-hours?${params.toString()}`);
      setLogs(data.logs); setTotalsUser(data.totals_by_user); setTotalsClient(data.totals_by_client);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters.client_id, filters.user_id, filters.date_from, filters.date_to]);

  const selectedClient = useMemo(() => clients.find((c) => c.id === form.client_id), [clients, form.client_id]);

  const submit = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      await api.post("/work-hours", {
        client_id: form.client_id, site_id: form.site_id || null, user_id: form.user_id, date: form.date,
        hours_worked: Number(form.hours_worked),
        movement_type: form.movement_type,
        normal_hours_manual: form.manual && form.normal_hours_manual !== "" ? Number(form.normal_hours_manual) : null,
        overtime_hours_manual: form.manual && form.overtime_hours_manual !== "" ? Number(form.overtime_hours_manual) : null,
        notes: form.notes || null,
      });
      toast.success("Jornada registrada");
      setForm({ ...empty, client_id: form.client_id, site_id: form.site_id, user_id: form.user_id });
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este registro de horas?")) return;
    try { await api.delete(`/work-hours/${id}`); toast.success("Registro eliminado"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.client_id) params.set("client_id", filters.client_id);
      if (filters.user_id) params.set("user_id", filters.user_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.settlement_status) params.set("settlement_status", filters.settlement_status);
      const res = await api.get(`/work-hours/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `horas_extra_${filters.date_from}_${filters.date_to}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Fix M (2026-09-04): exportación en PDF además del CSV, mismos filtros y datos.
  const exportPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.client_id) params.set("client_id", filters.client_id);
      if (filters.user_id) params.set("user_id", filters.user_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.settlement_status) params.set("settlement_status", filters.settlement_status);
      const res = await api.get(`/work-hours/export.pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `horas_extra_${filters.date_from}_${filters.date_to}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [recalcBusy, setRecalcBusy] = useState(false);
  const recalculate = async () => {
    if (!window.confirm("Esto va a recalcular las horas normales/extra de todos los registros que NO tengan corrección manual, usando la jornada estándar actual de cada objetivo. ¿Continuar?")) return;
    setRecalcBusy(true);
    try {
      const params = filters.client_id ? `?client_id=${filters.client_id}` : "";
      const { data } = await api.post(`/work-hours/recalculate${params}`);
      toast.success(`Recalculados ${data.recalculated} de ${data.checked} registros`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecalcBusy(false); }
  };

  // Fix Z (2026-09-14): saldo de horas extra (pago o devolución).
  const [settling, setSettling] = useState(null);
  const [settleForm, setSettleForm] = useState({ status: "pagada", date: todayStr(), notes: "" });
  const [settleBusy, setSettleBusy] = useState(false);
  const openSettle = (l) => { setSettling(l); setSettleForm({ status: "pagada", date: todayStr(), notes: "" }); };
  const submitSettle = async (e) => {
    e.preventDefault(); if (settleBusy) return; setSettleBusy(true);
    try {
      await api.patch(`/work-hours/${settling.id}/settlement`, settleForm);
      toast.success(settleForm.status === "pagada" ? "Jornada marcada como pagada" : "Jornada marcada como devuelta");
      setSettling(null); refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSettleBusy(false); }
  };
  const revertSettle = async (l) => {
    if (!window.confirm("¿Revertir el saldo de esta jornada? Vuelve a quedar pendiente.")) return;
    try { await api.post(`/work-hours/${l.id}/settlement/revert`); toast.success("Saldo revertido, vuelve a estar pendiente"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1200, margin: "0 auto" }} data-testid="work-hours-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Horas Extra</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>Pensado para objetivos tipo "Obra" (sin turnos fijos), pero disponible en cualquier objetivo. Configurá la jornada estándar en la ficha del cliente (pestaña Información) — lo que la exceda se cuenta como hora extra.</p>

      {/* Fix N (2026-09-04): solapa para ver, además de las jornadas, los francos
          adeudados por coberturas que absorbieron un franco. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18, borderBottom: "1px solid var(--invict-bone-3)" }} data-testid="wh-tabs">
        <button type="button" onClick={() => setTab("jornadas")} data-testid="wh-tab-jornadas"
          style={{ padding: "10px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: tab === "jornadas" ? "#0A0A0A" : "var(--invict-text-muted)", borderBottom: tab === "jornadas" ? "2px solid #BF3437" : "2px solid transparent" }}>
          Jornadas
        </button>
        <button type="button" onClick={() => setTab("francos")} data-testid="wh-tab-francos"
          style={{ padding: "10px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: tab === "francos" ? "#0A0A0A" : "var(--invict-text-muted)", borderBottom: tab === "francos" ? "2px solid #BF3437" : "2px solid transparent" }}>
          Francos adeudados
        </button>
      </div>

      {tab === "francos" ? (
        <DayOffDebt clients={clients} />
      ) : (
      <>
      <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="wh-form">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <div className="field"><label>Objetivo *</label>
            <select required value={form.client_id} onChange={(e) => { set("client_id", e.target.value); set("site_id", ""); }} data-testid="wh-client">
              <option value="">Elegir...</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <SiteSelect clientId={form.client_id} value={form.site_id} onChange={(v) => set("site_id", v)} testIdPrefix="wh"/>
          <div className="field"><label>Persona *</label>
            <select required value={form.user_id} onChange={(e) => set("user_id", e.target.value)} data-testid="wh-user">
              <option value="">Elegir...</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role}</option>)}
            </select>
          </div>
          <div className="field"><label>Fecha *</label><input required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="wh-date"/></div>
          <div className="field"><label>Horas trabajadas *</label><input required type="number" step="0.25" min="0" value={form.hours_worked} onChange={(e) => set("hours_worked", e.target.value)} data-testid="wh-hours-worked"/></div>
          <div className="field"><label>Tipo de movimiento *</label>
            <select required value={form.movement_type} onChange={(e) => set("movement_type", e.target.value)} data-testid="wh-movement-type">
              {Object.entries(MOVEMENT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        {/* Fix N (2026-09-04): las horas sueltas fuera de turno (espera de relevo /
            cobertura parcial) NO se calculan contra la jornada estándar: van íntegras
            a horas adicionales, aunque sean pocas (ej. 3 h de cobertura parcial). */}
        {form.movement_type !== "jornada_normal" && (
          <p style={{ fontSize: 11, color: "#BF3437", margin: "6px 0 0", fontWeight: 700 }} data-testid="wh-movement-hint">
            Estas horas se cuentan íntegras como adicionales, sin restarles la jornada estándar.
          </p>
        )}
        {selectedClient && (
          <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "6px 0 0" }}>
            Jornada estándar de este objetivo: <b>{selectedClient.standard_workday_hours ?? "12 (default, sin configurar)"} h</b>. Lo que exceda se calcula como hora extra automáticamente.
          </p>
        )}
        <div style={{ margin: "10px 0" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            <input type="checkbox" checked={form.manual} onChange={(e) => set("manual", e.target.checked)} data-testid="wh-manual-toggle"/>
            Corregir a mano (para casos que no encajan en la regla automática)
          </label>
        </div>
        {form.manual && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }} data-testid="wh-manual-fields">
            <div className="field"><label>Horas normales</label><input type="number" step="0.25" min="0" value={form.normal_hours_manual} onChange={(e) => set("normal_hours_manual", e.target.value)} data-testid="wh-normal-manual"/></div>
            <div className="field"><label>Horas extra</label><input type="number" step="0.25" min="0" value={form.overtime_hours_manual} onChange={(e) => set("overtime_hours_manual", e.target.value)} data-testid="wh-overtime-manual"/></div>
          </div>
        )}
        <div className="field"><label>Notas</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="wh-notes"/></div>
        <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="wh-submit"><Plus size={14} style={{ display: "inline", marginRight: 6 }}/>{busy ? "Registrando..." : "Registrar jornada"}</button>
      </form>

      {/* Filtros */}
      <div className="card" style={{ padding: 14, marginBottom: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }} data-testid="wh-filters">
        <div className="field"><label>Objetivo</label>
          <select value={filters.client_id} onChange={(e) => setF("client_id", e.target.value)} data-testid="wh-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Persona</label>
          <select value={filters.user_id} onChange={(e) => setF("user_id", e.target.value)} data-testid="wh-filter-user">
            <option value="">Todas</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Desde</label><input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="wh-filter-from"/></div>
        <div className="field"><label>Hasta</label><input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="wh-filter-to"/></div>
        <div className="field"><label>Estado de saldo</label>
          <select value={filters.settlement_status} onChange={(e) => setF("settlement_status", e.target.value)} data-testid="wh-filter-settlement">
            <option value="">Todos</option>
            {Object.entries(SETTLEMENT_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <button type="button" className="btn-ghost" onClick={exportCsv} style={{ alignSelf: "end", padding: "9px 14px" }} data-testid="wh-export-csv"><Download size={13} style={{ marginRight: 6 }}/>Exportar CSV</button>
        <button type="button" className="btn-ghost" onClick={exportPdf} style={{ alignSelf: "end", padding: "9px 14px" }} data-testid="wh-export-pdf"><Download size={13} style={{ marginRight: 6 }}/>Exportar PDF</button>
        {user?.role === "admin" && (
          <button type="button" className="btn-ghost" onClick={recalculate} disabled={recalcBusy} style={{ alignSelf: "end", padding: "9px 14px" }} data-testid="wh-recalculate" title="Recalcula horas normales/extra de registros sin corrección manual, con la jornada estándar actual">
            <RefreshCw size={13} style={{ marginRight: 6 }}/>{recalcBusy ? "Recalculando..." : "Recalcular jornadas"}
          </button>
        )}
      </div>

      {/* Acumulados */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Users size={13}/> Acumulado por persona</div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="wh-totals-user">
            {totalsUser.length === 0 && <div style={{ padding: 16, color: "var(--invict-text-muted)", fontSize: 12 }}>Sin datos en el período.</div>}
            {totalsUser.map((t) => (
              <div key={t.user_id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`wh-total-user-${t.user_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{t.user_name}</span>
                <span style={{ fontSize: 12 }}>{t.normal_hours} h normales · <b style={{ color: t.overtime_hours_pending > 0 ? "#BF3437" : "#0A0A0A" }}>{t.overtime_hours_pending} h extra pendiente</b> · {t.overtime_hours_settled} h saldada</span>
              </div>
              {Object.keys(t.by_type || {}).length > 1 && (
                <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 3 }} data-testid={`wh-total-user-bytype-${t.user_id}`}>
                  {Object.entries(t.by_type).map(([mt, v]) => `${MOVEMENT_TYPE_LABEL[mt] || mt}: ${(v.normal_hours + v.overtime_hours).toFixed(2)} h`).join(" · ")}
                </div>
              )}
            </div>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Building2 size={13}/> Acumulado por objetivo</div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="wh-totals-client">
            {totalsClient.length === 0 && <div style={{ padding: 16, color: "var(--invict-text-muted)", fontSize: 12 }}>Sin datos en el período.</div>}
            {totalsClient.map((t) => (
              <div key={t.client_id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", justifyContent: "space-between" }} data-testid={`wh-total-client-${t.client_id}`}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{t.client_name}</span>
                <span style={{ fontSize: 12 }}>{t.normal_hours} h normales · <b style={{ color: t.overtime_hours_pending > 0 ? "#BF3437" : "#0A0A0A" }}>{t.overtime_hours_pending} h extra pendiente</b> · {t.overtime_hours_settled} h saldada</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Clock size={13}/> Historial de jornadas</div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="wh-list">
        {logs.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin jornadas registradas en el período.</div>}
        {logs.map((l) => (
          <div key={l.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center" }} data-testid={`wh-row-${l.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{l.user_name}{l.user_deleted && <DeletedUserBadge id={l.id} />} · {l.client_name}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                {l.date} · {l.hours_worked} h trabajadas · {l.normal_hours} h normales · <b style={{ color: l.overtime_hours > 0 ? "#BF3437" : "inherit" }}>{l.overtime_hours} h extra</b>
                {l.is_manual_override && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, background: "var(--invict-bone-3)", padding: "1px 6px", borderRadius: 8 }} data-testid={`wh-manual-badge-${l.id}`}>Corrección manual</span>}
                {(l.movement_type && l.movement_type !== "jornada_normal") && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, background: "#fde8e8", color: "#BF3437", padding: "1px 6px", borderRadius: 8 }} data-testid={`wh-movement-badge-${l.id}`}>{MOVEMENT_TYPE_LABEL[l.movement_type] || l.movement_type}</span>}
                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 8, background: l.settlement_status === "pendiente" ? "#fff3cd" : "#e3f6e8", color: l.settlement_status === "pendiente" ? "#8a6300" : "#1e7a3a" }} data-testid={`wh-settlement-badge-${l.id}`}>
                  {SETTLEMENT_STATUS_LABEL[l.settlement_status] || "Pendiente"}{l.settlement_status !== "pendiente" && l.settlement_date ? ` el ${l.settlement_date}` : ""}
                </span>
              </div>
              {l.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{l.notes}</div>}
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }} data-testid={`wh-schedule-turno-${l.id}`}>
                Turno en el cronograma: {fmtScheduleTurno(l.schedule_turno)}
              </div>
              {l.settlement_status !== "pendiente" && l.settlement_notes && (
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }} data-testid={`wh-settlement-notes-${l.id}`}>Saldo: {l.settlement_notes} {l.settlement_registered_by_name ? `(registrado por ${l.settlement_registered_by_name})` : ""}</div>
              )}
            </div>
            {l.settlement_status === "pendiente" ? (
              <button onClick={() => openSettle(l)} className="btn-ghost" style={{ padding: "6px 10px", fontSize: 11, whiteSpace: "nowrap" }} data-testid={`wh-settle-open-${l.id}`}>Marcar saldo</button>
            ) : (
              <button onClick={() => revertSettle(l)} className="btn-ghost" style={{ padding: "6px 10px", fontSize: 11, whiteSpace: "nowrap" }} data-testid={`wh-settle-revert-${l.id}`}>Revertir</button>
            )}
            <button onClick={() => del(l.id)} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }} data-testid={`wh-delete-${l.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
      </>
      )}

      {settling && (
        <div className="modal-overlay" onClick={() => setSettling(null)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitSettle} data-testid="wh-settle-modal">
            <h3>Marcar saldo de horas extra</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{settling.user_name} · {settling.client_name} · {settling.date} · {settling.overtime_hours} h extra</p>
            <div className="field"><label>Estado *</label>
              <select value={settleForm.status} onChange={(e) => setSettleForm((f) => ({ ...f, status: e.target.value }))} data-testid="wh-settle-status">
                <option value="pagada">Pagada (se abonó en dinero)</option>
                <option value="devuelta">Devuelta (se compensó con tiempo)</option>
              </select>
            </div>
            <div className="field"><label>Fecha *</label><input required type="date" value={settleForm.date} onChange={(e) => setSettleForm((f) => ({ ...f, date: e.target.value }))} data-testid="wh-settle-date"/></div>
            <div className="field"><label>Observación</label><input value={settleForm.notes} onChange={(e) => setSettleForm((f) => ({ ...f, notes: e.target.value }))} data-testid="wh-settle-notes"/></div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setSettling(null)} style={{ flex: 1 }} data-testid="wh-settle-cancel">Cancelar</button>
              <button type="submit" disabled={settleBusy} className="btn-primary" style={{ flex: 1 }} data-testid="wh-settle-submit">{settleBusy ? "Guardando..." : "Guardar"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
